import {
  getVisibilityKeys,
  readVisibilityState,
  writeDynamicSettings,
  writeMode,
  DEFAULT_N,
} from "../../utils/visibilityState";
import { getHiddenForPresetAtDepth } from "../../WidgetBarChart/utils/getTopBottomDatapoints";

/**
 * Value selection for a categorical metric card, sharing the bar chart's
 * Show/Hide model (utils/visibilityState): a `mode` picks which of two
 * independent states drives the card.
 *
 *   manual   props.selectedFieldValue — the SELECTED ids (this widget predates
 *            the hidden-list convention and its saved configs, title
 *            generation and stat gating all read the selected list, so it
 *            stays). Absent key = every value; [] = nothing picked, but ONLY
 *            once this mode key exists — before the picker, [] was itself how
 *            "every value" was written, so an empty list with no mode recorded
 *            keeps that original meaning (see WidgetMetricCard/index.js).
 *            Write "every value" as the ABSENT key, never as [].
 *   dynamic  props.fieldValuesDynamicPreset ("topN" | "bottomN") and
 *            props.fieldValuesVisibilityN, resolved against the CURRENT
 *            fieldValueCounts at render so it never goes stale.
 *
 * The hidden key below is never written; it only seeds the derived mode /
 * preset key names so the two widgets stay on one vocabulary.
 */
export const VALUE_SELECTION_KEY_OVERRIDES = {
  hiddenKey: "props.hiddenFieldValues",
  nKey: "props.fieldValuesVisibilityN",
  presetKey: "props.fieldValuesVisibilityPreset",
};

export const VALUE_SELECTION_KEYS = getVisibilityKeys({
  keyOverrides: VALUE_SELECTION_KEY_OVERRIDES,
});

export const readValueSelectionState = (widgetConfig) =>
  readVisibilityState(widgetConfig, VALUE_SELECTION_KEYS);

/**
 * Is the stored selection "the user picked nothing"?
 *
 * The single source of truth for reading an EMPTY selection, because the card
 * and the picker must never disagree about one: if the card reads [] as "every
 * value" while the picker reads it as "none", the picker renders every box
 * unticked and the first mode write turns a working card blank.
 *
 * `mode` MUST be the raw stored props.fieldValuesVisibilityMode — do NOT route
 * it through readValueSelectionState / readVisibilityState, whose `mode`
 * DEFAULTS to "manual" when the key is absent. That default erases the only
 * thing distinguishing a config the picker wrote from one that predates it, and
 * reinstates the bug this guard exists to prevent.
 */
export const isNothingPicked = (selectedFieldValue, mode) =>
  Array.isArray(selectedFieldValue) &&
  selectedFieldValue.length === 0 &&
  mode === "manual";

export const writeValueSelectionMode = (w, mode) =>
  writeMode(w, VALUE_SELECTION_KEYS, mode);

export const writeValueSelectionDynamic = (w, { dynamicPreset, n }) =>
  writeDynamicSettings(w, VALUE_SELECTION_KEYS, { dynamicPreset, n });

/** The fieldValueCounts items inside the top / bottom N by count. */
export const resolveDynamicSelection = (fieldValueCounts, dynamicPreset, n) => {
  if (!Array.isArray(fieldValueCounts) || !fieldValueCounts.length) return [];
  const hidden = getHiddenForPresetAtDepth(
    fieldValueCounts,
    dynamicPreset || "topN",
    n || DEFAULT_N,
    1
  );
  const hiddenSet = new Set(hidden.map((h) => String(h.value)));
  return fieldValueCounts.filter((item) => !hiddenSet.has(String(item.value)));
};

export const getDynamicSelectionTitle = (fieldDisplay, dynamicPreset, n) =>
  `${fieldDisplay} - ${dynamicPreset === "bottomN" ? "Bottom" : "Top"} ${n || DEFAULT_N}`;
